import React from 'react';
import { cn } from './cn';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

/**
 * The two performance signals, kept visually distinct because they mean
 * different things (see scoring.py in the backend).
 *
 *   <ScoreBadge score={7.8} />   absolute — "this is a good post"
 *   <LiftBadge lift={3.2} .../>  relative — "this is unusual for this account"
 *
 * A post can carry both, and the interesting ones usually do. Collapsing them
 * into one number would lose the distinction that makes either useful: a 5.0
 * post lifting 3x is an ordinary post that suddenly found an audience, which
 * is a completely different thing from a 9.0 post doing its usual numbers.
 */

/** 0.0-10.0 absolute quality. Neutral chrome — the number carries the meaning. */
export const ScoreBadge = ({ score, className }) => {
  if (score === null || score === undefined) return null;
  // Only the top of the range earns emphasis; everything else stays quiet so
  // a list of ordinary posts does not look like a wall of alerts.
  const strong = score >= 7.5;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-pill px-2 py-[3px] text-caption-sm font-semibold tabular-nums',
        strong ? 'bg-ink text-ink-inverse' : 'bg-surface-raised text-ink-muted',
        className
      )}
      title={`Performance score ${score.toFixed(1)} out of 10 — absolute, comparable across accounts`}
    >
      {score.toFixed(1)}
    </span>
  );
};

const LIFT_STYLES = {
  breaking_out: { cls: 'bg-success-tint text-success', label: 'Breaking out' },
  above: { cls: 'bg-info-tint text-info', label: 'Above average' },
  below: { cls: 'bg-surface-raised text-ink-subtle', label: 'Below average' },
  normal: null, // the common case earns no badge at all
};

/** Relative-to-own-median lift. Carries an explicit word, never colour alone. */
export const LiftBadge = ({ lift, tier, compactLabel, className }) => {
  const style = LIFT_STYLES[tier];
  if (!style || lift === null || lift === undefined) return null;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-pill px-2 py-[3px] text-caption-sm font-semibold',
        style.cls,
        className
      )}
      title={`${lift.toFixed(1)}x this account's median for its recent posts`}
    >
      <span aria-hidden="true">{tier === 'below' ? '▾' : '▴'}</span>
      <span className="tabular-nums">{lift.toFixed(1)}×</span>
      {!compactLabel && <span className="font-medium">{style.label}</span>}
    </span>
  );
};

/**
 * How much history a figure rests on.
 *
 * Shown rather than hidden, because "2.4x" off three posts and "2.4x" off
 * twenty are not the same claim, and the reader cannot tell which they are
 * looking at otherwise. The backend reports the band; this just says it.
 */
export const HistoryNote = ({ history, postsCounted, className }) => {
  if (!history || history === 'full') return null;
  const text =
    history === 'insufficient'
      ? 'Not enough history yet'
      : `Based on ${postsCounted} post${postsCounted === 1 ? '' : 's'}`;
  return (
    <span
      className={cn('text-caption-sm text-ink-subtle', className)}
      title={
        history === 'insufficient'
          ? 'Fewer than 5 recent posts — a median off this few would be misleading, so no comparison is shown.'
          : 'Fewer than 20 recent posts, so the comparison baseline is shorter than usual.'
      }
    >
      {text}
    </span>
  );
};

/** "3h ago" / "just now" from an ISO timestamp. */
// An ISO string with no offset is parsed by `new Date()` as LOCAL time, not
// UTC -- so a UTC timestamp from two hours ago reads as eleven hours ago in
// UTC+9, or as the future in UTC-5. Everything the API sends is UTC, so an
// offset-less string gets one.
const asUtc = (iso) => (/[Zz]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);

const since = (iso) => {
  if (!iso) return null;
  const mins = Math.round((Date.now() - new Date(asUtc(iso)).getTime()) / 60000);
  if (!Number.isFinite(mins)) return null;
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

/**
 * Freshness plus an on-demand refresh.
 *
 * The point of showing WHEN is that it removes the reason to re-fetch on every
 * page open: if the reader can see the figures are from this morning, they do
 * not need the app to prove it by spending an API call.
 *
 * The button self-disables for the cooldown the server reports, so a 429 is
 * something the UI avoids rather than something it recovers from.
 */
export const RefreshControl = ({ lastRefreshedAt, stale, cooldown = 0, busy, onRefresh, className }) => {
  const [remaining, setRemaining] = React.useState(cooldown);

  React.useEffect(() => { setRemaining(cooldown); }, [cooldown]);
  React.useEffect(() => {
    if (remaining <= 0) return undefined;
    const t = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [remaining]);

  const when = since(lastRefreshedAt);
  const blocked = remaining > 0 || busy;

  return (
    <span className={cn('flex items-center gap-2', className)}>
      <span className={cn('text-caption-sm', stale ? 'text-warning' : 'text-ink-subtle')}>
        {when ? `Updated ${when}` : 'Never updated'}
      </span>
      <button
        type="button"
        onClick={onRefresh}
        disabled={blocked}
        aria-label="Refresh from the platform"
        title={
          remaining > 0
            ? `Refreshed recently — available again in ${remaining}s`
            : 'Fetch the latest figures from the platform'
        }
        className={cn(
          'inline-flex min-h-[var(--tap-target)] min-w-[var(--tap-target)] items-center justify-center',
          'rounded-pill text-ink-muted transition-colors sm:min-h-7 sm:min-w-7',
          blocked ? 'opacity-40' : 'hover:bg-surface-raised hover:text-ink'
        )}
      >
        <ArrowPathIcon className={cn('size-4', busy && 'animate-spin')} aria-hidden="true" />
      </button>
      {remaining > 0 && (
        <span className="text-caption-sm tabular-nums text-ink-subtle">{remaining}s</span>
      )}
    </span>
  );
};

/** Net engagement as a percentage. Null-safe: unknown shows an em dash. */
export const EngagementRate = ({ rate, className }) => (
  <span className={cn('tabular-nums', className)}>
    {rate === null || rate === undefined ? '—' : `${(rate * 100).toFixed(1)}%`}
  </span>
);
